import React from 'react';
import { useNotification } from '../../contexts/Notification';
import { Button, Snackbar, makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import Strapi from '../../services/strapi';
import { canPublish } from '../../services/authorization';
import { useAuthentication, Role } from '../../contexts/Authentication';
import { Share, Notif } from '../../interfaces/article';
import Fetcher from '../../services/fetcher';
import settings from '../../settings';

interface ArticleSnackbarProps {
    disabled: boolean,
    edtion_type: "edit" | "create",
    type: string,
    comment?: string,
    publish_date: null | Date,
    setPublish_date: (date: null | Date) => void,
    update: () => Promise<any>,
}

const useStyles = makeStyles({
    snackbarContent: {
        background: "rgba(255, 255, 255, 0.9)",
        padding: "5px",
        borderRadius: "5px"
    },
    warningComment: {
        background: "rgba(150, 0, 0, 0.12)",
        borderRadius: "4px",
        padding: "6px 16px",
        marginBottom: "4px",
        width: "max-content"
    },
    button: {
        marginBottom: "4px",
        display: "block"
    }
})

const share = (article: any, articleType: string) => {
    if ([Share.Share, Share.ShareNow].includes(Share[article.share as keyof typeof Share])) {
        return Fetcher.post(settings.sharingApi, JSON.stringify({
            article: article.slug,
            caption: article.title,
            type: articleType,
            priority: article.share === Share[Share.ShareNow],
            url: `${settings.frontUrl}article/${articleType}/${article.slug}`,
            publishedDate: (new Date(article.publish_date)).getTime(),
        }))
    }
    return Promise.resolve()
}

const notifMobile = (article: any, articleType: string) => {
    if ([Notif.Notif, Notif.NotifNow].includes(Notif[article.notif as keyof typeof Notif])) {
        return Fetcher.post(settings.notifApi, JSON.stringify({
            article: article.slug,
            caption: article.short_hat ?? article.long_hat,
            type: articleType,
            now: article.notif === Notif[Notif.NotifNow],
            url: `${settings.frontUrl}article/${articleType}/${article.slug}`,
            publishedDate: (new Date(article.publish_date)).getTime(),
        }))
    }
    return Promise.resolve()
}

const ArticleSnackbar = (props: ArticleSnackbarProps) => {
    const notification = useNotification();
    const history = useHistory();
    const classes = useStyles();
    const auth = useAuthentication();
    const [loading, setLoading] = React.useState(false)

    const update = async (isNew: boolean) => {
        if (isNew) notification.notify("creating...", "info");
        else notification.notify("updating...", "info");

        if (!loading) setLoading(true)
        try {
            const r = await props.update();
            setLoading(false);
            if (isNew) {
                notification.notify("article created");
                history.push(`/articles`);
            } else {
                notification.notify("article updated");
                history.push(`/articles/edit/${props.type}/${r.slug}/edit`);
            }
        } catch (e) {
            console.error("Fail to create article", {error: e});
            if (isNew) notification.notify("Fail to create article", "error");
            else notification.notify("Fail to update article", "error");
            setLoading(false);
        }
    }

    const updateAndPublish = async (isNew: boolean) => {
        if (isNew) notification.notify("creating...", "info");
        else notification.notify("updating...", "info");

        if (!loading) setLoading(true)
        try {
            const r = await props.update();
            if (isNew) notification.notify("article created");
            else notification.notify("article updated");

            const rp = await Strapi.put(`${props.type}s/${r.id}/publish`, {}, {
                publish_date: Date.now()
            }).catch(e => {
                notification.notify("Fail to publish");
            })
            notification.notify("Article published");

            if (!isNew) props.setPublish_date(new Date(rp.publish_date));

            await share(r, props.type).catch(e => {
                console.error(e);
                notification.notify("Fail to send to share queue", "error")
            })
            await notifMobile(r, props.type).catch(e => {
                console.error(e);
                notification.notify("Fail to send to mobile share queue", "error");
            });
            setLoading(false);
            history.push(`/articles`);
        } catch (e) {
            console.error("Fail to create article", {error: e});
            notification.notify("Fail to create article", "error");
            setLoading(false);
        };
    }

    return <Snackbar
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
        }}
        open={true}
    >
        {props.edtion_type === "edit" ?
            <div className={classes.snackbarContent}>
                {props.comment &&
                    <div className={classes.warningComment}>
                        {props.comment}
                    </div>
                }
                <Button
                    variant="contained"
                    disabled={loading || props.disabled}
                    color="primary"
                    onClick={() => update(false)}
                >{auth.role.includes(Role.Contributeur) ? "Proposer une révision" : "Update"}
                </Button>
                {props.publish_date === null && canPublish(auth.role) &&
                    <Button
                        className={classes.button}
                        variant="contained"
                        disabled={loading || props.disabled}
                        color="primary"
                        onClick={() => updateAndPublish(false)}
                    >Updater et publier maintenant
                    </Button>
                }
            </div>
            : <div className={classes.snackbarContent}>
                {props.comment &&
                    <div className={classes.warningComment}>
                        {props.comment}
                    </div>
                }
                <Button
                    className={classes.button}
                    variant="contained"
                    disabled={loading || props.disabled}
                    color="primary"
                    onClick={() => update(true)}
                >Create
                </Button>
                {canPublish(auth.role) && <Button
                    className={classes.button}
                    variant="contained"
                    disabled={loading || props.disabled}
                    color="primary"
                    onClick={() => updateAndPublish(true)}
                >Create et publier maintenant
                </Button>}
            </div>}
    </Snackbar>
};

export default ArticleSnackbar;